// Header scroll-state controller.
//
// Replaces Atelier's scroll-threshold-driven `.mbf-scroll-sticky` toggle
// with an IntersectionObserver on the hero element. This produces the
// behavior the brand spec wants:
//
//   * Hero IN viewport            → header is TRANSPARENT (over the hero).
//   * Hero OUT of viewport        → header switches to white-bg compact
//                                   state (`.mbf-scroll-sticky`), wordmark
//                                   collapses, only the eye-mark remains.
//   * User scrolls back UP into hero → header returns to transparent the
//                                   moment the hero crosses back into view.
//
// On pages without a `.mbf-hero-banner` (e.g. /about-us/, /contact-us/,
// /shop/), this bails — Atelier's default applies (which is white-bg
// sticky from the top, exactly what we want on non-hero pages).
//
// Why IntersectionObserver over scrollY math: scrollY thresholds drift
// when hero size changes (responsive heights, font-load reflow). IO fires
// on the actual visual crossing — pixel-accurate, viewport-aware, free.

export function initHeaderScrollState() {
	const hero   = document.querySelector('.mbf-hero-banner, .sentiments-hero-video');
	const header = document.querySelector('header.wp-block-template-part');
	if (!hero || !header) return;

	// One class hook drives the visual state:
	//   * `.is-past-hero` on <html> — Sentiments-owned, independent of
	//     Atelier's own scroll mechanics. CSS in header.scss + branding.scss
	//     keys the white bg + wordmark-collapse off this class only.
	//
	// Earlier this module also toggled Atelier's `.mbf-scroll-sticky` and
	// reset `data-mbf-navbar-smart-sticky=false`. That created two state
	// systems racing each other — Atelier's scrollY threshold toggled the
	// mbf class at a different scroll point than the hero IO, producing a
	// double-transition that looked like "two nav bars swapping". Now the
	// hero IO is the sole driver. Atelier's own classes can still toggle
	// underneath but no Sentiments rule reads them for visual state.
	const html = document.documentElement;

	let isCompact = false;
	const apply = (shouldBeCompact) => {
		if (shouldBeCompact === isCompact) return;
		isCompact = shouldBeCompact;
		if (shouldBeCompact) html.classList.add('is-past-hero');
		else                 html.classList.remove('is-past-hero');
	};

	// ── Strategy split: mobile vs desktop ───────────────────────────────
	//
	// Desktop: IntersectionObserver on the hero. The header stays
	// transparent while ANY of the hero is in view, then flips to white
	// when the hero is past the fold. Hero-anchored, pixel-accurate.
	//
	// Mobile / tablet (≤1024px): a scrollY threshold instead. The reason:
	// on a small viewport the hero is the full svh, and while it scrolls
	// out the wordmark stays visible over the section *below* the hero
	// (the dark Baiga imagery) — producing an unintended "translucent
	// over dark content" state that reads as a third nav state.
	// Collapsing to a scroll-based trigger means: at the very top the
	// header is transparent over the hero; the moment the user scrolls
	// AT ALL, the header flips to the white compact state. Two states,
	// no in-between window over dark body content. Breakpoint matches
	// the CSS in branding.scss so layout + behaviour switch in lockstep.
	const mobileMQ = window.matchMedia('(max-width: 1024px)');

	let cleanup = null;

	const setupDesktopIO = () => {
		// Observe the hero. Use a negative top margin equal to the header
		// height so the trigger lines up with the visual seam between
		// hero and header.
		const headerH = header.getBoundingClientRect().height || 64;
		const io = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					apply(!entry.isIntersecting);
				}
			},
			{
				root: null,
				rootMargin: `-${Math.round(headerH)}px 0px 0px 0px`,
				threshold: 0,
			}
		);
		io.observe(hero);

		// Re-measure header height on resize (font-load reflow, rotation).
		let resizeRaf = 0;
		const onResize = () => {
			if (resizeRaf) cancelAnimationFrame(resizeRaf);
			resizeRaf = requestAnimationFrame(() => {
				const h = header.getBoundingClientRect().height || 64;
				io.disconnect();
				io.unobserve && io.unobserve(hero);
				const io2 = new IntersectionObserver(
					(entries) => { for (const e of entries) apply(!e.isIntersecting); },
					{ root: null, rootMargin: `-${Math.round(h)}px 0px 0px 0px`, threshold: 0 }
				);
				io2.observe(hero);
			});
		};
		window.addEventListener('resize', onResize, { passive: true });

		return () => {
			io.disconnect();
			window.removeEventListener('resize', onResize);
		};
	};

	const setupMobileScroll = () => {
		// Threshold of 10px — covers minor scroll-overshoot on touch /
		// browser-bar collapse without firing prematurely on iOS rubber-
		// band overscroll-back-to-zero.
		const THRESHOLD = 10;
		const onScroll = () => apply(window.scrollY > THRESHOLD);
		window.addEventListener('scroll', onScroll, { passive: true });
		onScroll(); // initial — handles deep-linked / refreshed mid-scroll

		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	};

	const wire = () => {
		if (cleanup) { cleanup(); cleanup = null; }
		// Reset state when switching mode so we don't carry over a stale
		// `.is-past-hero` from the other branch.
		isCompact = false;
		html.classList.remove('is-past-hero');
		cleanup = mobileMQ.matches ? setupMobileScroll() : setupDesktopIO();
	};

	wire();
	// Switch modes when the viewport crosses the breakpoint (rotation,
	// devtools resize). matchMedia change is more reliable than a window
	// resize listener for breakpoint-crossing detection.
	if (mobileMQ.addEventListener) {
		mobileMQ.addEventListener('change', wire);
	} else if (mobileMQ.addListener) {
		mobileMQ.addListener(wire); // Safari < 14 fallback
	}
}
